import { FunctionComponent, useEffect, useState } from "react";
import { AppCalendar } from "./AppCalendar";
import * as datefns from "date-fns";
import { DateRange, Matcher, ModifiersClassNames } from "react-day-picker";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils/ui";
import { CalendarIcon } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { useWeekStartsOn } from "@/lib/dates/useWeekStartsOn";
import {
  getWeekDays,
  getWeekRange,
  realignWeekDays,
} from "@/lib/dates/weekStart";

interface WeekPickerProps {
  selectedDays: Date[];
  setSelectedDays: (days: Date[]) => void;
  additionalModifiers?: Record<string, Matcher | Matcher[]>;
  additionalClassNames?: ModifiersClassNames;
}

const WeekPicker: FunctionComponent<WeekPickerProps> = ({
  selectedDays,
  setSelectedDays,
  additionalModifiers,
  additionalClassNames,
}) => {
  const weekStartsOn = useWeekStartsOn();
  const [hoverRange, sethoverRange] = useState<DateRange>();
  const [visibleMonth, setVisibleMonth] = useState<Date | undefined>(
    selectedDays.length > 0 ? selectedDays[0] : undefined
  );
  const daysAreSelected = selectedDays.length > 0;

  useEffect(() => {
    setVisibleMonth(selectedDays.length > 0 ? selectedDays[0] : undefined);
    sethoverRange(undefined);
  }, [selectedDays]);

  useEffect(() => {
    if (selectedDays.length === 0) {
      return;
    }
    const realigned = realignWeekDays(selectedDays, weekStartsOn);
    const unchanged =
      realigned.length === selectedDays.length &&
      realigned.every((day, index) =>
        datefns.isSameDay(day, selectedDays[index]),
      );
    if (!unchanged) {
      setSelectedDays(realigned);
    }
  }, [selectedDays, setSelectedDays, weekStartsOn]);

  const modifiers: Record<string, Matcher | Matcher[]> = {
    ...additionalModifiers,
  };

  if (hoverRange?.from && hoverRange?.to) {
    const isHoveringSelectedWeek =
      daysAreSelected &&
      datefns.isSameDay(hoverRange.from, selectedDays[0]) &&
      datefns.isSameDay(hoverRange.to, selectedDays[6]);

    if (isHoveringSelectedWeek) {
      modifiers.hoverRangeSelected = hoverRange;
      modifiers.hoverRangeSelectedStart = hoverRange.from;
      modifiers.hoverRangeSelectedEnd = hoverRange.to;
    } else {
      modifiers.hoverRange = hoverRange;
      modifiers.hoverRangeStart = hoverRange.from;
      modifiers.hoverRangeEnd = hoverRange.to;
    }
  }

  if (daysAreSelected) {
    modifiers.selectedRange = {
      from: selectedDays[0],
      to: selectedDays[6],
    };
    modifiers.selectedRangeStart = selectedDays[0];
    modifiers.selectedRangeEnd = selectedDays[6];
  }

  const modifierClassNames: ModifiersClassNames = {
    selectedRange: "!rounded-none !bg-primary !text-white ",
    hoverRange:
      "!rounded-none !bg-secondary !text-secondary-foreground text-left",
    hoverRangeStart: "!rounded-l-md",
    hoverRangeEnd: "!rounded-r-md",
    hoverRangeSelected: "!rounded-none !bg-primary !text-white text-left",
    hoverRangeSelectedStart: "!rounded-l-md",
    hoverRangeSelectedEnd: "!rounded-r-md",
    selectedRangeStart: "!rounded-l-md",
    selectedRangeEnd: "!rounded-r-md",
    ...additionalClassNames,
  };

  const handleDayChange = (date: Date) => {
    sethoverRange(undefined);
    setSelectedDays(getWeekDays(date, weekStartsOn));
  };

  const handleDayEnter = (date: Date) => {
    sethoverRange(getWeekRange(date, weekStartsOn));
  };

  const handleDayLeave = () => {
    sethoverRange(undefined);
  };

  const handleWeekClick = (
    _weekNumber: number,
    days: Date[],
    _event: React.MouseEvent
  ) => {
    sethoverRange(undefined);
    setSelectedDays(days);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className={cn(
            "w-[240px] pl-3 font-normal",
            selectedDays.length === 0 && "text-muted-foreground"
          )}
        >
          {selectedDays.length > 0 ? (
            <>
              {datefns.format(selectedDays[0], "do MMM")} -{" "}
              {datefns.format(selectedDays[6], "do MMM")}
            </>
          ) : (
            <span>Pick a date</span>
          )}
          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <AppCalendar
          month={visibleMonth}
          onMonthChange={setVisibleMonth}
          mode="multiple"
          showOutsideDays
          selected={selectedDays}
          modifiers={modifiers}
          modifiersClassNames={modifierClassNames}
          onWeekNumberClick={handleWeekClick}
          onDayMouseEnter={handleDayEnter}
          onDayMouseLeave={handleDayLeave}
          onDayClick={handleDayChange}
          classNames={{
            day_today: "bg-accent text-accent-foreground !rounded-md",
            day: cn(
              buttonVariants({ variant: "ghost" }),
              "h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-none hover:bg-transparent hover:text-foreground transition-all"
            ),
            day_button:
              "!bg-transparent hover:!bg-transparent hover:!text-inherit data-[selected-single=true]:!bg-transparent data-[selected-single=true]:!text-inherit data-[range-middle=true]:!bg-transparent data-[range-middle=true]:!text-inherit data-[range-start=true]:!bg-transparent data-[range-start=true]:!text-inherit data-[range-end=true]:!bg-transparent data-[range-end=true]:!text-inherit [&[data-logged=true]]:relative [&[data-logged=true]]:after:absolute [&[data-logged=true]]:after:bottom-1.5 [&[data-logged=true]]:after:left-1/2 [&[data-logged=true]]:after:h-1 [&[data-logged=true]]:after:w-1 [&[data-logged=true]]:after:-translate-x-1/2 [&[data-logged=true]]:after:rounded-full [&[data-logged=true]]:after:bg-primary [&[data-logged=true]]:after:content-[''] [&[data-logged=true][data-selected-single=true]]:after:bg-white [&[data-logged=true][data-selected-range=true]]:after:bg-white [&[data-logged=true][data-selected-range-start=true]]:after:bg-white [&[data-logged=true][data-selected-range-end=true]]:after:bg-white",
            day_range_end: "rounded-r-md",
            day_range_start: "rounded-l-md",
            weeknumber: "text-muted-foreground p-0 h-9 w-9 font-normal",
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export { getWeekDays, getWeekRange };

export default WeekPicker;
